import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';

interface ApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (comment?: string) => void;
  onReject: (reason: string) => void;
  entryData: {
    category: string;
    value: number;
    unit: string;
    uploadedBy?: string;
    uploadedAt?: string;
  };
}

export const ApprovalDialog: React.FC<ApprovalDialogProps> = ({
  open,
  onClose,
  onApprove,
  onReject,
  entryData,
}) => {
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (action === 'reject' && !comment.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    if (action === 'approve') {
      onApprove(comment || undefined);
    } else if (action === 'reject') {
      onReject(comment);
    }

    // Reset state
    setAction(null);
    setComment('');
    setError('');
    onClose();
  };

  const handleCancel = () => {
    setAction(null);
    setComment('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action === null && 'Review Data Submission'}
        {action === 'approve' && 'Approve Submission'}
        {action === 'reject' && 'Reject Submission'}
      </DialogTitle>

      <DialogContent>
        {/* Entry Details */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Submission Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Category:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {entryData.category}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Value:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {entryData.value.toLocaleString()} {entryData.unit}
              </Typography>
            </Box>
            {entryData.uploadedBy && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Uploaded By:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entryData.uploadedBy}
                </Typography>
              </Box>
            )}
            {entryData.uploadedAt && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Uploaded At:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {new Date(entryData.uploadedAt).toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Action Selection */}
        {action === null && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => setAction('approve')}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#45a049' },
              }}
            >
              Approve
            </Button>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Cancel />}
              onClick={() => setAction('reject')}
              sx={{
                backgroundColor: '#f44336',
                '&:hover': { backgroundColor: '#da190b' },
              }}
            >
              Reject
            </Button>
          </Box>
        )}

        {/* Comment/Reason Field */}
        {action !== null && (
          <>
            <Alert
              severity={action === 'approve' ? 'success' : 'error'}
              sx={{ mb: 2 }}
            >
              {action === 'approve'
                ? 'This data will be approved and included in reports'
                : 'This data will be rejected and sent back to the contributor'}
            </Alert>

            <TextField
              label={action === 'approve' ? 'Comment (optional)' : 'Rejection Reason *'}
              multiline
              rows={4}
              fullWidth
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setError('');
              }}
              error={!!error}
              helperText={error}
              placeholder={
                action === 'approve'
                  ? 'Add any comments about this approval...'
                  : 'Please explain why this submission is being rejected...'
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        {action !== null && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: action === 'approve' ? '#4caf50' : '#f44336',
              '&:hover': {
                backgroundColor: action === 'approve' ? '#45a049' : '#da190b',
              },
            }}
          >
            Confirm {action === 'approve' ? 'Approval' : 'Rejection'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
